import { useState, useMemo } from 'react';
import { Search, FileText, FolderOpen, ChevronRight, ChevronDown } from 'lucide-react';
import { promptMetaTemplate } from '../../data/meta';
import {
  deployModule,
  apiModule,
  typesModule,
  uiModule,
  pitfallsModule,
  packageManagerModule,
} from '../../data/modules';
import { landingArchetype, cmsArchetype } from '../../data/archetypes';
import {
  layer1Template as marvelandLayer1,
  layer2Template as marvelandLayer2,
  layer3Template as marvelandLayer3,
  layer4Template as marvelandLayer4,
} from '../../data/projects/marveland';
import {
  layer1Template as dikoLayer1,
  layer2Template as dikoLayer2,
  layer3Template as dikoLayer3,
  layer4Template as dikoLayer4,
} from '../../data/projects/diko';
import {
  layer1Template as doudouLayer1,
  layer2Template as doudouLayer2,
  layer3Template as doudouLayer3,
  layer4Template as doudouLayer4,
} from '../../data/projects/doudou';

interface TreeNode {
  id: string;
  label: string;
  type: 'folder' | 'file';
  content?: string;
  children?: TreeNode[];
}

const knowledgeTree: TreeNode[] = [
  {
    id: 'meta',
    label: 'Meta',
    type: 'folder',
    children: [
      { id: 'meta-template', label: 'Prompt Meta Template', type: 'file', content: promptMetaTemplate },
    ],
  },
  {
    id: 'modules',
    label: '通用模組',
    type: 'folder',
    children: [
      { id: 'deploy', label: 'Deploy', type: 'file', content: deployModule },
      { id: 'api', label: 'API', type: 'file', content: apiModule },
      { id: 'types', label: 'Types', type: 'file', content: typesModule },
      { id: 'ui', label: 'UI', type: 'file', content: uiModule },
      { id: 'pitfalls', label: 'Pitfalls', type: 'file', content: pitfallsModule },
      { id: 'package-manager', label: 'Package Manager', type: 'file', content: packageManagerModule },
    ],
  },
  {
    id: 'archetypes',
    label: '原型模板',
    type: 'folder',
    children: [
      {
        id: 'landing',
        label: 'Landing Page',
        type: 'folder',
        children: [
          { id: 'landing-layer1', label: 'Layer 1 — Skeleton', type: 'file', content: landingArchetype.layerTemplates[0] },
          { id: 'landing-layer2', label: 'Layer 2 — Frontend', type: 'file', content: landingArchetype.layerTemplates[1] },
          { id: 'landing-layer3', label: 'Layer 3 — Admin', type: 'file', content: landingArchetype.layerTemplates[2] },
          { id: 'landing-layer4', label: 'Layer 4 — Deploy', type: 'file', content: landingArchetype.layerTemplates[3] },
        ],
      },
      {
        id: 'cms',
        label: 'CMS Website',
        type: 'folder',
        children: [
          { id: 'cms-layer1', label: 'Layer 1 — Skeleton', type: 'file', content: cmsArchetype.layerTemplates[0] },
          { id: 'cms-layer2', label: 'Layer 2 — Frontend', type: 'file', content: cmsArchetype.layerTemplates[1] },
          { id: 'cms-layer3', label: 'Layer 3 — Admin', type: 'file', content: cmsArchetype.layerTemplates[2] },
          { id: 'cms-layer4', label: 'Layer 4 — Deploy', type: 'file', content: cmsArchetype.layerTemplates[3] },
        ],
      },
    ],
  },
  {
    id: 'projects',
    label: '專案模板',
    type: 'folder',
    children: [
      {
        id: 'marveland',
        label: 'Marveland',
        type: 'folder',
        children: [
          { id: 'marveland-layer1', label: 'Layer 1 — Skeleton', type: 'file', content: marvelandLayer1 },
          { id: 'marveland-layer2', label: 'Layer 2 — Frontend', type: 'file', content: marvelandLayer2 },
          { id: 'marveland-layer3', label: 'Layer 3 — Admin', type: 'file', content: marvelandLayer3 },
          { id: 'marveland-layer4', label: 'Layer 4 — Deploy', type: 'file', content: marvelandLayer4 },
        ],
      },
      {
        id: 'diko',
        label: 'Diko',
        type: 'folder',
        children: [
          { id: 'diko-layer1', label: 'Layer 1 — Skeleton', type: 'file', content: dikoLayer1 },
          { id: 'diko-layer2', label: 'Layer 2 — Frontend', type: 'file', content: dikoLayer2 },
          { id: 'diko-layer3', label: 'Layer 3 — Admin', type: 'file', content: dikoLayer3 },
          { id: 'diko-layer4', label: 'Layer 4 — Deploy', type: 'file', content: dikoLayer4 },
        ],
      },
      {
        id: 'doudou',
        label: 'Doudou',
        type: 'folder',
        children: [
          { id: 'doudou-layer1', label: 'Layer 1 — Skeleton', type: 'file', content: doudouLayer1 },
          { id: 'doudou-layer2', label: 'Layer 2 — Frontend', type: 'file', content: doudouLayer2 },
          { id: 'doudou-layer3', label: 'Layer 3 — Admin', type: 'file', content: doudouLayer3 },
          { id: 'doudou-layer4', label: 'Layer 4 — Deploy', type: 'file', content: doudouLayer4 },
        ],
      },
    ],
  },
];

function flattenNodes(nodes: TreeNode[]): TreeNode[] {
  const result: TreeNode[] = [];
  for (const node of nodes) {
    result.push(node);
    if (node.children) result.push(...flattenNodes(node.children));
  }
  return result;
}

function findNodeById(nodes: TreeNode[], id: string): TreeNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

function renderMarkdown(content: string): string {
  let html = escapeHtml(content);

  html = html
    .replace(/^### (.*$)/gim, '<h3 class="text-lg font-bold mt-4 mb-2">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-6 mb-3">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-8 mb-4">$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code class="px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-sm">$1</code>')
    .replace(/\n/g, '<br/>');

  return html;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function KnowledgeBrowser() {
  const [selectedId, setSelectedId] = useState<string>('meta-template');
  const [expanded, setExpanded] = useState<Set<string>>(
    new Set(['meta', 'modules', 'archetypes', 'landing', 'cms', 'projects', 'marveland', 'diko', 'doudou'])
  );
  const [search, setSearch] = useState('');

  const allNodes = useMemo(() => flattenNodes(knowledgeTree), []);
  const selectedNode = findNodeById(knowledgeTree, selectedId);

  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    const lower = search.toLowerCase();
    return allNodes.filter((node) => node.type === 'file' && node.content?.toLowerCase().includes(lower));
  }, [search, allNodes]);

  const toggleExpanded = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const renderTree = (nodes: TreeNode[], depth = 0) => {
    return nodes.map((node) => {
      const isExpanded = expanded.has(node.id);
      const isSelected = selectedId === node.id;

      if (node.type === 'folder') {
        return (
          <div key={node.id} style={{ marginLeft: depth * 12 }}>
            <button
              type="button"
              onClick={() => toggleExpanded(node.id)}
              className="flex items-center gap-1 w-full px-2 py-1.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <FolderOpen size={14} className="text-yellow-500" />
              {node.label}
            </button>
            {isExpanded && node.children && (
              <div className="mt-0.5">{renderTree(node.children, depth + 1)}</div>
            )}
          </div>
        );
      }

      return (
        <button
          key={node.id}
          type="button"
          onClick={() => setSelectedId(node.id)}
          style={{ marginLeft: depth * 12 }}
          className={`flex items-center gap-1 w-full px-2 py-1.5 text-sm rounded ${
            isSelected
              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
              : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          <FileText size={14} className="text-slate-400" />
          {node.label}
        </button>
      );
    });
  };

  return (
    <div className="h-full flex flex-col md:flex-row gap-4">
      <div className="w-full md:w-72 shrink-0 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3 overflow-auto">
        <div className="relative mb-3">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索知識庫..."
            className="w-full pl-8 pr-3 py-2 text-sm rounded border bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-600"
          />
        </div>

        {search ? (
          <div className="space-y-1">
            {searchResults.length === 0 && (
              <p className="text-sm text-slate-500 dark:text-slate-400 p-2">無匹配結果</p>
            )}
            {searchResults.map((node) => (
              <button
                key={node.id}
                type="button"
                onClick={() => {
                  setSelectedId(node.id);
                  setSearch('');
                }}
                className="flex items-center gap-1 w-full px-2 py-1.5 text-sm text-left text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
              >
                <FileText size={14} className="text-slate-400" />
                {node.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-0.5">{renderTree(knowledgeTree)}</div>
        )}
      </div>

      <div className="flex-1 min-h-0 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
          <h3 className="font-semibold text-slate-900 dark:text-white">
            {selectedNode?.label || '請選擇文件'}
          </h3>
        </div>
        <div className="flex-1 overflow-auto p-4 md:p-6">
          {selectedNode?.content ? (
            <div
              className="prose dark:prose-invert max-w-none text-sm md:text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedNode.content) }}
            />
          ) : (
            <p className="text-slate-500 dark:text-slate-400">請從左側選擇一個知識庫文件</p>
          )}
        </div>
      </div>
    </div>
  );
}
